import React, { useState, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import Dropzone from 'react-dropzone';
import { showToast } from 'stores/ToastStore';
import { CUSTOM, PROOF_OF_DELIVERY, shipmentDocTypes } from 'config/documents';
import { X, Upload, LoaderCircle } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Input, } from '@cargomatic/ui';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { useCombineDocuments, useHandleBulkUploadDocuments, useBulkProcessShipment, } from 'utils/DocumentsHooks';
import { attachDocuments, RecognitionFieldTypes, waitDocumentUpload, } from 'utils/DocumentsAPIUtils';
import { UPLOAD_TYPES } from 'constants/Documents';
import { getFilesWithContentType } from 'utils/files/files';
function BulkUploadDocumentsModel({ t, isOpen, onClose, shipments }) {
    const [file, setFile] = useState(null);
    const [podProcessing, setPodProcessing] = useState(false);
    const [documentType, setDocumentType] = useState('');
    const [documentCustomName, setDocumentCustomName] = useState('');
    const [bulkProcessDocument] = useBulkProcessShipment();
    const [combineDocuments] = useCombineDocuments();
    const { handleUpload, uploading } = useHandleBulkUploadDocuments(shipments, null, async (shipments, stop, type, uploadedData, userVisibility) => {
        if (type === PROOF_OF_DELIVERY.value) {
            setPodProcessing(true);
            let combinedDocument = await combineDocuments(uploadedData.map((document) => document.id), type, PROOF_OF_DELIVERY.name);
            const shipmentIds = shipments === null || shipments === void 0 ? void 0 : shipments.map((shipment) => shipment.id);
            await waitDocumentUpload(combinedDocument.data.key);
            await attachDocuments(combinedDocument.data.id, shipmentIds, UPLOAD_TYPES.BULK);
            await bulkProcessDocument(combinedDocument.data.id, shipmentIds, {
                recognitionFieldsType: RecognitionFieldTypes.GENERIC_UPLOAD_POD,
            });
            uploadedData = [combinedDocument.data];
            setPodProcessing(false);
        }
        if (onClose) {
            showToast(t('DOCUMENT_UPLOADED_SUCCESSFULLY'), t('DOCUMENT_UPLOADED_SUCCESSFULLY'), 'success');
            return onClose(shipments, stop, type, uploadedData, userVisibility);
        }
    }, async () => {
        if (onClose)
            onClose();
    });
    const onDrop = useCallback((acceptedFiles) => {
        const acceptedFilesWithContentTypes = getFilesWithContentType(acceptedFiles);
        setFile(acceptedFilesWithContentTypes[0]);
    }, []);
    const handleSave = async () => {
        var _a;
        if (file && documentType) {
            try {
                const filename = documentType === CUSTOM.value
                    ? documentCustomName
                    : ((_a = file === null || file === void 0 ? void 0 : file.name) !== null && _a !== void 0 ? _a : documentType);
                const renamedFiles = new File([file], filename.toString(), {
                    type: file.type,
                });
                await handleUpload([renamedFiles], documentType, undefined, true);
            }
            catch (_error) {
                showToast(t('DOCUMENT_UPLOAD_FAILED_RETRY'), t('DOCUMENT_UPLOAD_FAILED_RETRY'), 'error');
            }
        }
    };
    const enableSubmitBtn = !file ||
        !documentType ||
        uploading ||
        podProcessing ||
        (documentType === 'custom' && !(documentCustomName === null || documentCustomName === void 0 ? void 0 : documentCustomName.length));
    return (React.createElement(Dialog, { open: isOpen, onOpenChange: onClose },
        React.createElement(DialogContent, { className: "sm:max-w-[425px]" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, t('BULK_UPLOAD_DOCUMENT')),
                React.createElement(Label, { className: "block text-sm font-medium text-gray-700" }, t('BULK_UPLOAD_DOCUMENT_NOTE_WITH_COUNT', {
                    count: (shipments === null || shipments === void 0 ? void 0 : shipments.length) || 0,
                }))),
            React.createElement("div", { className: "grid gap-4 py-4" },
                React.createElement(Label, { className: "block text-sm font-medium text-gray-700 -mb-2" },
                    t('UPLOAD_DOCUMENT'),
                    "*"),
                React.createElement(Dropzone, { onDrop: onDrop, accept: ACCEPTED_FILE_TYPES, multiple: false, className: "border-2 border-dashed rounded-md p-4 text-center cursor-pointer" }, file ? (React.createElement("div", { className: "flex items-center justify-between" },
                    React.createElement("span", null, file.name),
                    React.createElement(Button, { variant: "ghost", onClick: (e) => {
                            e.stopPropagation();
                            setFile(null);
                        } },
                        React.createElement(X, { className: "h-4 w-4" })))) : (React.createElement("div", null,
                    React.createElement(Upload, { className: "mx-auto h-5 w-5 text-gray-400" }),
                    React.createElement("p", { className: "text-sm mt-2" }, t('DRAG_AND_DROP_BULK_UPLOAD_NOTE'))))),
                React.createElement("div", { className: "space-y-2" },
                    React.createElement(Label, { htmlFor: "documentType", className: "block text-sm font-medium text-gray-700" },
                        t('SELECT_DOCUMENT_TYPE'),
                        "*"),
                    React.createElement(Select, { value: documentType, onValueChange: (value) => {
                            setDocumentType(value);
                        } },
                        React.createElement(SelectTrigger, { className: "w-full" },
                            React.createElement(SelectValue, { placeholder: t('SELECT_DOCUMENT_TYPE') })),
                        React.createElement(SelectContent, null, shipmentDocTypes === null || shipmentDocTypes === void 0 ? void 0 : shipmentDocTypes.map((item, index) => (React.createElement(SelectItem, { key: `index-${index}-dropdown-document-types`, value: item.value }, t(item.localizationSlug))))))),
                documentType === CUSTOM.value && (React.createElement("div", { className: "space-y-2" },
                    React.createElement(Label, { htmlFor: "custom-input", className: "block text-sm font-medium text-gray-700" },
                        t('CUSTOM_TITLE'),
                        "*"),
                    React.createElement(Input, { id: "custom-input", placeholder: t('ENTER_DOCUMENT_TITLE'), value: documentCustomName, onChange: (event) => { var _a; return setDocumentCustomName((_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.value); } })))),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { type: "submit", onClick: handleSave, disabled: enableSubmitBtn, className: "flex items-center justify-center gap-2" },
                    uploading && React.createElement(LoaderCircle, { className: "w-4 h-4 animate-spin" }),
                    t('SAVE'))))));
}
export default withTranslation()(BulkUploadDocumentsModel);
